import { z } from 'zod';

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export const PosSessionStatusSchema = z.enum(['open', 'closed', 'reconciled']);
export type PosSessionStatus = z.infer<typeof PosSessionStatusSchema>;

export const PosSaleStatusSchema = z.enum([
  'parked',
  'finalized',
  'voided',
  'refunded',
  'partially_refunded',
]);
export type PosSaleStatus = z.infer<typeof PosSaleStatusSchema>;

export const PosTenderKindSchema = z.enum(['cash', 'card', 'wallet', 'cashback', 'other']);
export type PosTenderKind = z.infer<typeof PosTenderKindSchema>;

export const PosLineDiscountKindSchema = z.enum(['none', 'percent', 'fixed']);
export type PosLineDiscountKind = z.infer<typeof PosLineDiscountKindSchema>;

// -----------------------------------------------------------------------------
// Primitives (mirror the conventions in installments.ts / documents.ts)
// -----------------------------------------------------------------------------

const positiveDecimal = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative decimal');
/**
 * A cart line's quantity: positive to sell, NEGATIVE to take back, ZERO to
 * quote.
 *
 * A customer who buys ten and hands two back is one transaction at the counter,
 * so it is one sale here — the negative line restocks and reverses its own
 * revenue and cost, and the till nets the two into a single amount to collect.
 *
 * ZERO is a PRICE-ONLY line, and shops ask for it: the item stays on the ticket
 * with its price, and nothing is charged, nothing leaves the shelf and nothing
 * reaches the ledger (the journal is built from money-bearing lines only). It
 * used to be refused as "a mistake being saved", which is true of an accident
 * and wrong about a customer asking what three things would cost.
 *
 * The SIGN is all this allows. Whether the cashier may take goods back is a
 * permission (`pos.refund`), and whether the whole cart may end up negative — or
 * add up to nothing at all — is a rule the finalize step enforces. Neither
 * belongs in a regex.
 */
const signedQuantity = z
  .string()
  .regex(/^-?\d+(\.\d{1,4})?$/, 'expected a decimal');
const signedDecimal = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'expected decimal (can be negative)');
const rate = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative rate (0..n)');
const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);
const uuid = z.string().uuid();

// -----------------------------------------------------------------------------
// Terminals
// -----------------------------------------------------------------------------

export const PosTerminalCreateSchema = z.object({
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(120),
  location: z.string().max(200).nullable().optional(),
  warehouseId: uuid.nullable().optional(),
  /**
   * Every OTHER warehouse this till may sell from. The default above is always
   * allowed; this is the rest of the shelf it can reach. Omit = leave as is,
   * `[]` = back to the default only.
   */
  warehouseIds: z.array(uuid).max(50).optional(),
  /** Sell from ANY warehouse — for a counter that should not be re-listed every time a warehouse is added. */
  allWarehouses: z.boolean().optional(),
  /** Which cashiers may open this till. Empty = anyone with the permission. */
  cashierUserIds: z.array(uuid).max(100).optional(),
  defaultCurrencyCode: currencyCode,
  defaultCashAccountId: uuid.nullable().optional(),
  defaultCardClearingAccountId: uuid.nullable().optional(),
  defaultWalletClearingAccountId: uuid.nullable().optional(),
  receiptHeader: z.string().max(2_000).nullable().optional(),
  receiptFooter: z.string().max(2_000).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type PosTerminalCreate = z.infer<typeof PosTerminalCreateSchema>;

export const PosTerminalUpdateSchema = PosTerminalCreateSchema.partial();
export type PosTerminalUpdate = z.infer<typeof PosTerminalUpdateSchema>;

export const PosTerminalSchema = z.object({
  id: uuid,
  tenantId: uuid,
  code: z.string(),
  name: z.string(),
  location: z.string().nullable(),
  warehouseId: uuid.nullable(),
  /** The full allowed set, default first — what the register's picker offers. */
  warehouseIds: z.array(uuid),
  allWarehouses: z.boolean(),
  cashierUserIds: z.array(uuid),
  defaultCurrencyCode: z.string(),
  defaultCashAccountId: uuid.nullable(),
  defaultCardClearingAccountId: uuid.nullable(),
  defaultWalletClearingAccountId: uuid.nullable(),
  receiptHeader: z.string().nullable(),
  receiptFooter: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PosTerminal = z.infer<typeof PosTerminalSchema>;

// -----------------------------------------------------------------------------
// Sessions
// -----------------------------------------------------------------------------

export const PosSessionOpenSchema = z.object({
  terminalId: uuid,
  openingCash: positiveDecimal,
  currencyCode: currencyCode.optional(),
});
export type PosSessionOpen = z.infer<typeof PosSessionOpenSchema>;

/**
 * One currency's drawer reconciliation for a shift. A shift can take money in
 * the session currency AND in a customer group's own currency; those notes live
 * in separate compartments and are counted separately. Amounts are NEVER summed
 * across currencies.
 */
export const PosDrawerCountSchema = z.object({
  currencyCode: z.string(),
  /** Opening float — non-zero only for the session currency. */
  openingCash: z.string(),
  /** Opening float + cash tenders − change given, in THIS currency. */
  expected: z.string(),
  /** What the cashier physically counted in THIS currency. */
  counted: z.string(),
  /** counted − expected, in THIS currency. */
  variance: z.string(),
});
export type PosDrawerCount = z.infer<typeof PosDrawerCountSchema>;

export const PosSessionCloseSchema = z.object({
  /**
   * The SESSION-currency count. Required for backward compatibility — every
   * existing client (web POS, the offline terminal, mandob, the AI tool) sends
   * exactly this and nothing else.
   */
  closingCash: positiveDecimal,
  /**
   * Per-currency counts for a mixed-currency shift. An entry for the session
   * currency overrides `closingCash`; a currency the shift took money in but
   * that is absent here counts as 0 (a full short, not a hidden success).
   */
  countedByCurrency: z
    .array(z.object({ currencyCode, amount: positiveDecimal }))
    .max(20)
    .optional(),
  note: z.string().max(500).nullable().optional(),
});
export type PosSessionClose = z.infer<typeof PosSessionCloseSchema>;

export const PosSessionSchema = z.object({
  id: uuid,
  tenantId: uuid,
  sessionNumber: z.string(),
  terminalId: uuid,
  cashierUserId: uuid,
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  openingCash: z.string(),
  /** Session-currency figures (kept for every existing reader). */
  expectedCash: z.string().nullable(),
  closingCash: z.string().nullable(),
  variance: z.string().nullable(),
  /**
   * Full per-currency drawer reconciliation, set at close. `null` on open
   * shifts and on shifts closed before per-currency counting existed — readers
   * fall back to the three scalar fields above (session currency only).
   */
  drawerCounts: z.array(PosDrawerCountSchema).nullable(),
  currencyCode: z.string(),
  status: PosSessionStatusSchema,
  closingNote: z.string().nullable(),
  closedByUserId: uuid.nullable(),
  /**
   * Money-bearing sales rung up on the shift, and what they took PER CURRENCY
   * (never one summed figure — a shift can sell in a customer group's own
   * currency). Set by the LIST endpoint only, so the office's shifts page can
   * show them without a Z-report call per row; optional so every other reader
   * of a session is unchanged.
   */
  salesCount: z.number().int().optional(),
  takings: z.array(z.object({ currencyCode: z.string(), total: z.string() })).optional(),
  /** Who opened (and owns) the shift — LIST endpoint only, like the two above. */
  cashierName: z.string().nullable().optional(),
  /**
   * LIST endpoint only, like the above: how the takings were paid, per currency
   * and tender (refunds and change netted in), the returns rung up (positive amounts) and
   * the customers' debt payments (per currency of the money that came in).
   */
  tenders: z.array(z.object({ currencyCode: z.string(), kind: PosTenderKindSchema, count: z.number().int(), total: z.string() })).optional(),
  returns: z.array(z.object({ currencyCode: z.string(), count: z.number().int(), total: z.string() })).optional(),
  collections: z.array(z.object({ currencyCode: z.string(), method: z.string(), count: z.number().int(), total: z.string() })).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PosSession = z.infer<typeof PosSessionSchema>;

// Z-report — aggregated end-of-shift summary
export const PosZReportSchema = z.object({
  session: PosSessionSchema,
  saleCount: z.number().int(),
  refundCount: z.number().int(),
  grossSales: z.string(),
  refundsTotal: z.string(),
  netSales: z.string(),
  taxCollected: z.string(),
  byTender: z.array(
    z.object({
      kind: PosTenderKindSchema,
      count: z.number().int(),
      total: z.string(),
    }),
  ),
  expectedCash: z.string(),
  countedCash: z.string().nullable(),
  variance: z.string().nullable(),
  /**
   * PER-CURRENCY breakdown of the same shift. A shift can now hold sales in a
   * customer group's own currency (e.g. USD alongside IQD), and money in
   * different currencies must NEVER be summed. The flat fields above stay
   * denominated in the SESSION currency; anything else lives here.
   *
   * Optional only so a client kept talking to an older API still parses; the
   * current server always populates it (at minimum the session-currency row).
   */
  byCurrency: z.array(
    z.object({
      currencyCode: z.string(),
      saleCount: z.number().int(),
      refundCount: z.number().int(),
      grossSales: z.string(),
      refundsTotal: z.string(),
      netSales: z.string(),
      taxCollected: z.string(),
      byTender: z.array(
        z.object({ kind: PosTenderKindSchema, count: z.number().int(), total: z.string() }),
      ),
      /** Opening float in this currency — non-zero only for the session currency. */
      openingCash: z.string(),
      /** Opening float + cash tenders − change, for this currency (drawer reconciliation). */
      expectedCash: z.string(),
      /** What was counted at close in this currency; null while the shift is open. */
      countedCash: z.string().nullable(),
      /** countedCash − expectedCash; null while the shift is open. */
      variance: z.string().nullable(),
      /**
       * Where the drawer's cash in THIS currency came from and went to — the
       * lines the close-out shows: notes received (incl. those taken for sales in
       * other currencies), how much of that was for other-currency sales, how
       * much of THIS currency's sales was paid in other notes, change given and
       * cash refunded. expected = opening + received − change − refunds.
       */
      cashReceived: z.string().optional(),
      fromOtherSales: z.string().optional(),
      paidInOtherNotes: z.string().optional(),
      changeGiven: z.string().optional(),
      cashRefunds: z.string().optional(),
      /** Returns rung up in this currency (negative sales), as a positive amount. */
      returnsTotal: z.string().optional(),
      /** Change given on this currency's own sales — inside its cash tender (handed over, not kept). */
      tenderChange: z.string().optional(),
      /**
       * Customers paying off their debt at the register, in THIS currency's
       * money: cash notes (already inside cashReceived and expected) and by
       * card / wallet / other (not in the drawer), with how many payments.
       */
      collectedCash: z.string().optional(),
      collectedOther: z.string().optional(),
      collectionsCount: z.number().int().optional(),
    }),
  ).optional(),
});
export type PosZReport = z.infer<typeof PosZReportSchema>;

/**
 * A customer paying off their debt at the register, in an open shift.
 * `amount` is what came in, in `currencyCode`'s money; `debtCurrencyCode` the
 * balance it pays (dinars for a dollar debt → converted at today's rate).
 * A MINUS amount is money handed to the customer out of the drawer, added to
 * what they owe — in that money's own currency.
 */
export const PosCollectCreateSchema = z.object({
  partyId: z.string().uuid(),
  amount: signedDecimal,
  currencyCode,
  debtCurrencyCode: currencyCode.optional(),
  method: z.enum(['cash', 'card', 'wallet', 'other']).default('cash'),
  reference: z.string().max(64).optional(),
  note: z.string().max(500).optional(),
});
export type PosCollectCreate = z.infer<typeof PosCollectCreateSchema>;

export interface PosCollection {
  id: string;
  sessionId: string;
  paymentId: string;
  paymentNumber: string;
  partyId: string;
  partyName: string | null;
  method: 'cash' | 'card' | 'wallet' | 'other';
  /** The debt it paid. */
  currencyCode: string;
  amount: string;
  /** The money that came in (another currency's when paid in it). */
  notesCurrency: string;
  notesAmount: string;
  createdAt: string;
  createdByName: string | null;
}

// -----------------------------------------------------------------------------
// Sale lines
// -----------------------------------------------------------------------------

export const PosLineDiscountSchema = z.object({
  kind: PosLineDiscountKindSchema,
  value: positiveDecimal,
});
export type PosLineDiscount = z.infer<typeof PosLineDiscountSchema>;

export const PosSaleLineAddSchema = z
  .object({
    itemId: uuid.optional(),
    // Packaging unit (item_units row) to sell by. Sets price + conversion
    // factor; only valid alongside itemId. Omitted → base unit.
    itemUnitId: uuid.optional(),
    barcode: z.string().max(64).optional(),
    description: z.string().max(500).optional(),
    quantity: signedQuantity.optional(),
    // The PRICE stays positive on a return line: a returned pen is worth what
    // it was sold for. The quantity carries the direction, not the price.
    unitPriceOverride: positiveDecimal.optional(),
    lineDiscount: PosLineDiscountSchema.optional(),
    taxRate: rate.optional(),
    warehouseId: uuid.nullable().optional(),
  })
  .refine((v) => v.itemId || v.barcode || v.description, {
    message: 'one of itemId / barcode / description is required',
  });
export type PosSaleLineAdd = z.infer<typeof PosSaleLineAddSchema>;

export const PosSaleLineUpdateSchema = z.object({
  quantity: signedQuantity.optional(),
  unitPriceOverride: positiveDecimal.optional(),
  lineDiscount: PosLineDiscountSchema.optional(),
  taxRate: rate.optional(),
});
export type PosSaleLineUpdate = z.infer<typeof PosSaleLineUpdateSchema>;

export const PosSaleLineSchema = z.object({
  id: uuid,
  saleId: uuid,
  lineNo: z.number().int(),
  itemId: uuid.nullable(),
  itemUnitId: uuid.nullable(),
  unitName: z.string().nullable(),
  unitFactor: z.string(),
  description: z.string().nullable(),
  quantity: z.string(),
  unitPrice: z.string(),
  lineDiscountKind: PosLineDiscountKindSchema,
  lineDiscountValue: z.string(),
  lineSubtotal: z.string(),
  taxRate: z.string(),
  taxAmount: z.string(),
  lineTotal: z.string(),
  warehouseId: uuid.nullable(),
  refundOfLineId: uuid.nullable(),
  createdAt: z.string(),
});
export type PosSaleLine = z.infer<typeof PosSaleLineSchema>;

// -----------------------------------------------------------------------------
// Tenders
// -----------------------------------------------------------------------------

export const PosTenderAddSchema = z.object({
  kind: PosTenderKindSchema,
  amount: signedDecimal, // refunds use negative
  currencyCode: currencyCode.optional(), // defaults to sale's currency
  reference: z.string().max(120).nullable().optional(),
  cardBrand: z.string().max(32).nullable().optional(),
  cardLast4: z.string().length(4).regex(/^\d{4}$/).nullable().optional(),
  approvalCode: z.string().max(64).nullable().optional(),
  cashAccountId: uuid.nullable().optional(),
});
export type PosTenderAdd = z.infer<typeof PosTenderAddSchema>;

export const PosTenderSchema = z.object({
  id: uuid,
  saleId: uuid,
  tenderNo: z.number().int(),
  kind: PosTenderKindSchema,
  amount: z.string(),
  currencyCode: z.string(),
  reference: z.string().nullable(),
  cardBrand: z.string().nullable(),
  cardLast4: z.string().nullable(),
  approvalCode: z.string().nullable(),
  cashAccountId: uuid.nullable(),
  receivedAt: z.string(),
});
export type PosTender = z.infer<typeof PosTenderSchema>;

// -----------------------------------------------------------------------------
// Sales
// -----------------------------------------------------------------------------

/**
 * The day a sale is BOOKED on (`YYYY-MM-DD`): its invoice, payments, cost of
 * sales and cashback post on it. Omitted / null = the day it is finalized.
 * Needs `pos.sale.date` and may not lie in the future.
 */
export const posSaleDocDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

export const PosSaleCreateSchema = z.object({
  sessionId: uuid,
  partyId: uuid.optional(), // omitted → walk-in
  docDate: posSaleDocDate.nullable().optional(),
  currencyCode: currencyCode.optional(), // omitted → session/terminal default
  notes: z.string().max(2_000).nullable().optional(),
  holdLabel: z.string().max(120).nullable().optional(),
  lines: z.array(PosSaleLineAddSchema).optional(),
});
export type PosSaleCreate = z.infer<typeof PosSaleCreateSchema>;

export const PosSaleUpdateSchema = z.object({
  partyId: uuid.optional(),
  /**
   * Re-denominate the parked sale. Only the SESSION currency or the attached
   * customer's group currency (`price_lists.currency_code`) is accepted — that's
   * how a قرطاسية customer flips the sale to USD after being selected. Existing
   * lines are RE-PRICED server-side from that customer's group prices; a line
   * with no group price in the target currency rejects the switch (422
   * `line_price_unavailable_in_currency`). Refused once tenders exist.
   */
  currencyCode: currencyCode.optional(),
  /** Back-date the sale (see `posSaleDocDate`); `null` puts it back on the finalize day. */
  docDate: posSaleDocDate.nullable().optional(),
  notes: z.string().max(2_000).nullable().optional(),
  holdLabel: z.string().max(120).nullable().optional(),
});
export type PosSaleUpdate = z.infer<typeof PosSaleUpdateSchema>;

export const PosSaleDiscountSchema = z.object({
  // Header-level discount applied to the subtotal. `null` clears it.
  discount: PosLineDiscountSchema.nullable(),
});
export type PosSaleDiscount = z.infer<typeof PosSaleDiscountSchema>;

export const PosSaleSchema = z.object({
  id: uuid,
  tenantId: uuid,
  saleNumber: z.string(),
  sessionId: uuid,
  terminalId: uuid,
  cashierUserId: uuid,
  partyId: uuid,
  couponId: uuid.nullable(),
  couponCode: z.string().nullable(),
  saleDate: z.string(),
  /** The day somebody chose to book the sale on (`YYYY-MM-DD`); null = the finalize day. */
  docDate: z.string().nullable().optional(),
  currencyCode: z.string(),
  subtotal: z.string(),
  discountTotal: z.string(),
  taxTotal: z.string(),
  total: z.string(),
  tenderTotal: z.string(),
  changeDue: z.string(),
  status: PosSaleStatusSchema,
  refundedTotal: z.string(),
  parentSaleId: uuid.nullable(),
  postedJournalEntryId: uuid.nullable(),
  invoiceId: uuid.nullable(),
  finalizedAt: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidedByUserId: uuid.nullable(),
  voidReason: z.string().nullable(),
  notes: z.string().nullable(),
  holdLabel: z.string().nullable(),
  /** The sale's branch (header warehouse) — null on pre-branches history. */
  warehouseId: uuid.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Distinct tender kinds on the sale (cash/card/…), in tender order — only on
   * LIST responses, where fetching each sale's tender rows would be N queries.
   * The detail endpoint returns the full tenders instead.
   */
  tenderKinds: z.array(PosTenderKindSchema).optional(),
  /**
   * Paid in OTHER notes than the sale's currency (web register): what came in
   * and the change that went back. Null on a sale paid in its own notes.
   */
  /** The customer's name — LIST rows only (the office's shift dialog). */
  partyName: z.string().nullable().optional(),
  cashIn: z.object({ currencyCode: z.string(), amount: z.string() }).nullable().optional(),
  cashBack: z.object({ currencyCode: z.string(), amount: z.string() }).nullable().optional(),
  /** How `cashIn` was paid: null/'cash' = notes; 'card' | 'wallet' | 'other' = charged in that currency. */
  cashInMethod: z.string().nullable().optional(),
});
export type PosSale = z.infer<typeof PosSaleSchema>;

/**
 * Finalize with money in another currency: what was handed over (`tendered`) and
 * the change given back (`change`, in the sale's or the tendered currency — the
 * register gives it in the shop's base one). The sale carries ONE tender of
 * `tenderKind` in its own currency for what that money pays; this says what it
 * really was. Cash by default; a card / wallet / other tender charged in the
 * other currency gives no change (0).
 */
export const PosCashExchangeSchema = z.object({
  tendered: z.object({ currencyCode, amount: positiveDecimal }),
  change: z.object({ currencyCode, amount: positiveDecimal }),
  tenderKind: z.enum(['cash', 'card', 'wallet', 'other']).optional(),
});
export type PosCashExchange = z.infer<typeof PosCashExchangeSchema>;

export const PosSaleWithDetailsSchema = z.object({
  sale: PosSaleSchema,
  lines: z.array(PosSaleLineSchema),
  tenders: z.array(PosTenderSchema),
});
export type PosSaleWithDetails = z.infer<typeof PosSaleWithDetailsSchema>;

// -----------------------------------------------------------------------------
// Customer at the counter — GET /pos/customers/:partyId/insight
// -----------------------------------------------------------------------------

/** The price THIS customer last paid for one (item, packaging unit, currency). */
export interface PosCustomerLastPrice {
  itemId: string;
  itemUnitId: string | null;
  currencyCode: string;
  unitPrice: string;
  soldAt: string;
  docNumber: string;
}

export interface PosCustomerRecentSale {
  source: 'pos' | 'invoice';
  docNumber: string;
  docDate: string;
  total: string;
  currencyCode: string;
  status: string;
}

export interface PosCustomerInsight {
  lastPrices: PosCustomerLastPrice[];
  recentSales: PosCustomerRecentSale[];
}

// -----------------------------------------------------------------------------
// Lifecycle bodies
// -----------------------------------------------------------------------------

export const PosSaleVoidSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type PosSaleVoid = z.infer<typeof PosSaleVoidSchema>;

/**
 * Undoing a void. `allowNegativeStock` is a deliberate override, never a
 * default: a restore normally refuses when the goods have been sold since,
 * because putting the sale back would take stock the shop does not have. When
 * the sale really happened, the goods really left and it is the STOCK FIGURE
 * that is wrong — the void received back what never came back — so the honest
 * result is negative, and this is how somebody says so on purpose.
 */
/** Re-opening a finished sale to correct it: why, for the audit trail. */
export const PosSaleReopenSchema = PosSaleVoidSchema;
export type PosSaleReopen = z.infer<typeof PosSaleReopenSchema>;

export const PosSaleUnvoidSchema = PosSaleVoidSchema.extend({
  allowNegativeStock: z.boolean().optional(),
});
export type PosSaleUnvoid = z.infer<typeof PosSaleUnvoidSchema>;

// Finalize takes no body — the server validates `tender_total >= total`
// from current state. Returns the finalized PosSaleWithDetails.

export const PosRefundLineSchema = z.object({
  refundOfLineNo: z.number().int().min(1),
  quantity: positiveDecimal,
});
export type PosRefundLine = z.infer<typeof PosRefundLineSchema>;

export const PosRefundCreateSchema = z.object({
  sessionId: uuid,
  reason: z.string().min(1).max(500),
  lines: z.array(PosRefundLineSchema).min(1),
  tenders: z.array(PosTenderAddSchema).min(1),
});
export type PosRefundCreate = z.infer<typeof PosRefundCreateSchema>;

// -----------------------------------------------------------------------------
// Settings (tenant-level POS GL mapping)
// -----------------------------------------------------------------------------

export const PosSettingsSchema = z.object({
  defaultTerminalId: uuid.nullable(),
  cardClearingAccountId: uuid.nullable(),
  walletClearingAccountId: uuid.nullable(),
  otherClearingAccountId: uuid.nullable(),
  cogsAccountId: uuid.nullable(),
  inventoryAccountId: uuid.nullable(),
});
export type PosSettings = z.infer<typeof PosSettingsSchema>;

export const PosSettingsUpdateSchema = PosSettingsSchema.partial();
export type PosSettingsUpdate = z.infer<typeof PosSettingsUpdateSchema>;
